/*
 * rvt-rs browser viewer — main thread.
 *
 * Responsibilities:
 *   - drag-and-drop / file-picker intake (VW1-23)
 *   - spin up the parse worker (VW1-19)
 *   - Three.js scene + orbit controls + GLTFLoader (VW1-03)
 *   - scene-tree / category / info panels
 *   - status line + error surfacing
 *
 * Kept deliberately dependency-light: Three.js + the wasm package,
 * plus a single on-page CSS block. No React, no UI framework.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  countGlbTriangles,
  logMemProfileCheckpoint,
  type MemProfileCheckpoint,
} from './memory-profile';
import { ChunkManager, type ChunkManifestEntry } from './chunk-manager';

// PER-1191 — main-thread memory checkpoint. `viewer/vite.config.ts`
// serves COOP/COEP headers on both the dev server and `preview`, so
// the page is `crossOriginIsolated` and `measureUserAgentSpecificMemory()`
// is available in Chromium: it measures the whole agent cluster (JS
// heap + workers + WASM linear memory), unlike `usedJSHeapSize`.
// Firefox/WebKit don't implement it — those report `null` here, same
// as the worker-side checkpoints report `wasm-memory-buffer` there.
// Verified while building this: the API also throws `SecurityError`
// under Playwright's default *headless* Chromium even though the page
// is crossOriginIsolated — it only resolves under `--headed`. A
// `null` here from an ordinary (headless) run is expected on every
// engine, Chromium included; see docs/web-memory-profile-2026-08-18.md.
async function measureMainThreadMemory(): Promise<{ bytes: number | null; metric: string | null }> {
  const measurer = (
    performance as unknown as {
      measureUserAgentSpecificMemory?: () => Promise<{ bytes: number }>;
    }
  ).measureUserAgentSpecificMemory;
  if (typeof measurer !== 'function') return { bytes: null, metric: null };
  try {
    const result = await measurer.call(performance);
    return { bytes: result.bytes, metric: 'measureUserAgentSpecificMemory' };
  } catch {
    // API present but denied — page not crossOriginIsolated, a
    // permission policy blocks it, or (verified) headless Chromium
    // rejects it outright. Report no number rather than a wrong one.
    return { bytes: null, metric: null };
  }
}

// ---------- DOM ----------
const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element: #${id}`);
  return el;
};
const viewport = $('viewport');
const dropzone = $('dropzone');
const fileInput = $('file-input') as HTMLInputElement;
const pickBtn = $('pick-file');
const statusEl = $('status');
const fileMetaEl = $('file-meta');
const supportedFormatsEl = $('supported-formats-text');
const treeEl = $('tree');
const categoriesEl = $('categories');
const infoEl = $('info');
const scheduleEl = $('schedule-summary');
const statusPanelEl = $('status-panel');
const diagnosticsJsonEl = $('diagnostics-json');
const downloadDiagnosticsBtn = $('download-diagnostics') as HTMLButtonElement;
const exportGlbBtn = $('export-glb') as HTMLButtonElement;
const exportIfcBtn = $('export-ifc') as HTMLButtonElement;
const exportSvgBtn = $('export-svg') as HTMLButtonElement;
const shareLinkBtn = $('share-link') as HTMLButtonElement;
const exportQualityEl = $('export-quality');

// ---------- Three.js scene ----------
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x0b0e13);
const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 2000);
camera.position.set(60, 40, 60);
const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
viewport.appendChild(renderer.domElement);
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.target.set(0, 0, 0);

// Test-only camera introspection/control hook (PER-1209): lets
// Playwright read the live camera position and displace it away
// from the auto-framed pose, so the share-link round-trip test can
// assert the restored camera actually matches the sender's, instead
// of only asserting the category filter. No production code path
// reads this.
declare global {
  interface Window {
    __rvtViewerCamera?: {
      position(): [number, number, number];
      nudge(dx: number, dy: number, dz: number): void;
    };
  }
}
window.__rvtViewerCamera = {
  position: () => [camera.position.x, camera.position.y, camera.position.z],
  nudge: (dx, dy, dz) => {
    camera.position.x += dx;
    camera.position.y += dy;
    camera.position.z += dz;
    controls.update();
  },
};

const hemi = new THREE.HemisphereLight(0xd7dce3, 0x0b0e13, 0.8);
scene.add(hemi);
const dir = new THREE.DirectionalLight(0xffffff, 0.7);
dir.position.set(50, 80, 50);
scene.add(dir);
const grid = new THREE.GridHelper(100, 20, 0x1d2430, 0x11161d);
scene.add(grid);
const axes = new THREE.AxesHelper(10);
scene.add(axes);

// PER-1218 phase 2 — geometry no longer arrives as one `currentModel`
// group; chunk roots are added/removed from `scene` directly by the
// chunk manager. Category visibility and raycasting operate on
// `scene.children` instead (see `applyCategoryVisibility`, the
// pointerdown handler above).
//
// The DXF underlay path (PER-1220) is a separate, much smaller
// pipeline than the RVT/IFC worker flow above — it never goes through
// the chunk manager — so it still needs its own single-group model
// and a generic "frame the camera on this object" helper. Scoped to
// this path only; kept out of the streaming code above deliberately.
let dxfUnderlayGroup: THREE.Group | null = null;

function frameCamera(obj: THREE.Object3D): void {
  const box = new THREE.Box3().setFromObject(obj);
  if (box.isEmpty()) return;
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  const dist = Math.abs(maxDim / Math.sin(fov / 2)) * 0.8;
  camera.position.copy(center).add(new THREE.Vector3(1, 0.8, 1).normalize().multiplyScalar(dist));
  controls.target.copy(center);
  camera.near = maxDim / 100;
  camera.far = dist * 10;
  camera.updateProjectionMatrix();
}

function disposeDxfUnderlayGroup(): void {
  if (!dxfUnderlayGroup) return;
  scene.remove(dxfUnderlayGroup);
  dxfUnderlayGroup.traverse((obj) => {
    const line = obj as THREE.LineSegments;
    if (line.geometry) line.geometry.dispose();
    const mat = line.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else if (mat) (mat as THREE.Material).dispose();
  });
  dxfUnderlayGroup = null;
}

function resize(): void {
  const w = viewport.clientWidth;
  const h = viewport.clientHeight;
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
}
window.addEventListener('resize', resize);
resize();

function tick(): void {
  controls.update();
  if (manifestLoaded) {
    chunkManager.update();
    syntheticChunkManager?.update();
  }
  renderer.render(scene, camera);
  requestAnimationFrame(tick);
}
tick();

// ---------- Raycasting for element picking ----------
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();

renderer.domElement.addEventListener('pointerdown', (ev) => {
  // PER-1218 phase 2 — there is no longer a single `currentModel`
  // group holding every element: geometry arrives as separate
  // per-chunk roots added directly to `scene` by the chunk manager
  // (each tagged `userData.chunkId`; the `entityIndex` on their
  // descendants is already the real global index — Rust's
  // `modelToGlbChunk` reports it directly, see `chunking.rs`).
  // Raycast the whole scene and take the first hit that actually
  // carries an `entityIndex` —
  // that skips the grid/axes/lights and any LOD proxy box (which
  // carries `userData.isLodProxy` but no `entityIndex`).
  if (!manifestLoaded) return;
  const rect = renderer.domElement.getBoundingClientRect();
  pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
  pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);
  const hits = raycaster.intersectObjects(scene.children, true);
  const hit = hits.find((h) => (h.object.userData as { entityIndex?: number }).entityIndex !== undefined);
  if (!hit) return;
  const userData = hit.object.userData as { entityIndex?: number };
  showElementInfo(userData.entityIndex!);
});

// ---------- Status ----------
function setStatus(text: string): void {
  statusEl.textContent = text;
}

// ---------- Worker ----------
type Worker_ = Worker & {
  postMessage: (data: unknown, transfer?: Transferable[]) => void;
};
let worker: Worker_ | null = null;
function resetWorker(): Worker_ {
  if (worker) worker.terminate();
  const w = new Worker(new URL('./worker.ts', import.meta.url), {
    type: 'module',
  }) as Worker_;
  worker = w;
  return w;
}

// ---------- Chunk streaming (PER-1218 phase 2) ----------
// One long-lived ChunkManager for the page; `setManifest` resets it
// for each newly loaded file (mirrors `clearLoadedFileState` for the
// other per-file state below). `requestChunk` always goes through the
// module-level `worker` binding, not a captured reference, so it
// keeps working across `resetWorker()` calls on every new file load.
const chunkManager = new ChunkManager(scene, camera, {
  requestChunk: (chunkId, entityIndices) => {
    worker?.postMessage({ type: 'fetchChunk', chunkId, entityIndices });
  },
});
let manifestLoaded = false;

// Test-only hook (PER-1218 phase 2), same spirit as
// `window.__rvtViewerCamera` above: no production code path reads
// this. It exists because no real `.rvt` fixture with multiple
// triangle-budgeted chunks is available in this environment (the one
// real multi-element file we have, ~25 elements / 288 triangles, is
// far under the 250,000-triangle chunk budget and would only ever
// produce a single chunk) — so multi-chunk load/evict/LOD behavior
// can only be exercised end-to-end by injecting a synthetic manifest
// and synthetic per-chunk GLB bytes directly at this layer, bypassing
// the real worker/wasm decode pipeline entirely.
declare global {
  interface Window {
    __rvtViewerChunks?: {
      residentCount(): number;
      residentBytes(): number;
      chunkCount(): number;
      injectSyntheticManifest(
        manifest: ChunkManifestEntry[],
        chunkGlbByIndex: Record<number, Uint8Array>,
      ): void;
    };
  }
}
// A throwaway manager wired to a synthetic in-memory responder instead
// of `worker.postMessage`, so injected tests never touch the real
// worker or wasm module. Created lazily by `injectSyntheticManifest`
// so it doesn't exist (and doesn't shadow the real chunkManager) until
// a test actually asks for it.
let syntheticChunkManager: ChunkManager | null = null;
window.__rvtViewerChunks = {
  residentCount: () => (syntheticChunkManager ?? chunkManager).residentChunkCount(),
  residentBytes: () => (syntheticChunkManager ?? chunkManager).residentBytesTotal(),
  chunkCount: () => (syntheticChunkManager ?? chunkManager).residentChunkCount(),
  injectSyntheticManifest: (manifest, chunkGlbByIndex) => {
    syntheticChunkManager = new ChunkManager(scene, camera, {
      requestChunk: (chunkId) => {
        const glb = chunkGlbByIndex[chunkId];
        if (glb) syntheticChunkManager?.onChunkBytes(chunkId, glb);
      },
      // Test-only: the real 1.5 GB `MEMORY_BUDGET_BYTES` would never
      // be exceeded by the tiny synthetic GLB fixtures an e2e test
      // can reasonably construct, so eviction would never be
      // observable. A deliberately tiny budget here — smaller than
      // even one synthetic chunk's byte length — lets
      // `chunk-streaming.spec.ts` actually exercise the
      // evict-on-camera-move path without needing gigabytes of fake
      // data. This constant exists only in this test seam, never on
      // the production `chunkManager` above.
      memoryBudgetBytes: 10,
    });
    syntheticChunkManager.setManifest(manifest);
    manifestLoaded = true;
  },
};

// ---------- Model / scene-graph state ----------
interface IfcModel {
  project?: { name?: string; description?: string };
  storeys?: Array<{ name: string; elevation?: number }>;
  // `kind` is `IfcEntity`'s serde tag (`#[serde(tag = "kind")]` in
  // `src/ifc/entities.rs`) — only the `"BuildingElement"` variant
  // carries `ifc_type`/`solid_shape`, so callers must check `kind`
  // before reading either.
  entities?: Array<{
    kind: string;
    name?: string;
    ifc_type?: string;
    guid?: string;
    solid_shape?: unknown;
  }>;
  // Present on models read from `.ifc` via `read_ifc_bytes` — the IFC
  // schema version ("IFC4"/"IFC2X3"), not the Revit project
  // description. See `IfcModel::description` in `src/ifc/mod.rs`.
  description?: string;
}
interface SceneNode {
  name: string;
  ifc_type: string;
  entity_index: number | null;
  children: SceneNode[];
}
interface ElementInfoPanel {
  name: string;
  ifc_type: string;
  type_guid?: string | null;
  storey_name?: string | null;
  storey_elevation_feet?: number | null;
  location_feet?: [number, number, number] | null;
  rotation_radians?: number | null;
  material_name?: string | null;
  properties: [string, string][];
}
// Mirrors `rvt::ifc::camera::CameraState` (VW1-07) — same field
// names/units (feet, radians) so `serde_wasm_bindgen` round-trips
// this object through `cameraEye`/`encodeToFragment` unchanged.
interface CameraState {
  target: [number, number, number];
  distance: number;
  yaw: number;
  pitch: number;
  fov_radians: number;
  near: number;
  far: number;
}
// Mirrors `rvt::ifc::share::ViewerState` (VW1-24). Only the fields
// this viewer actually populates are declared here — the Rust
// struct defaults the rest (`section_box`, `selected_name`,
// `file_hash`) on decode.
interface ViewerState {
  camera: CameraState;
  view_mode: 'plan' | 'three_d' | 'section';
  category_filter: { hidden: string[] };
}
interface ExportDiagnostics {
  input?: {
    revit_version?: number;
    project_name?: string;
    stream_count?: number;
    has_basic_file_info?: boolean;
    has_part_atom?: boolean;
    has_formats_latest?: boolean;
    has_global_latest?: boolean;
  };
  decoded?: {
    production_walker_elements?: number;
    diagnostic_proxy_candidates?: number;
    arcwall_records?: number;
  };
  confidence?: {
    level?: string;
    score?: number;
    has_typed_elements?: boolean;
    has_geometry?: boolean;
    warning_count?: number;
  };
  exported?: {
    building_elements?: number;
    building_elements_with_geometry?: number;
  };
  unsupported_features?: string[];
  warnings?: string[];
  format?: 'rvt';
}

/// `.ifc` (IFC2x3/IFC4 STEP) inputs (PER-1219) never produce
/// Revit-shaped `ExportDiagnostics` — there is no CFB container, no
/// stream count, no ArcWall records to report. Mirrors the CLI's
/// `IfcInspectReport` (`src/bin/rvt_inspect.rs`) rather than
/// pretending an IFC read went through the Revit path: a file that
/// opened and decoded fine must never be told apart as "parser bug,
/// please report".
/// One construction element the `.ifc` file named that produced no
/// geometry, and why (PER-1270 GRAVE 2) — mirrors Rust's
/// `ifc::reader::DroppedElement`.
interface DroppedElement {
  express_id: number;
  ifc_type: string;
  reason: string;
}

interface IfcDiagnostics {
  format: 'ifc';
  schema_version: string | null;
  building_elements: number;
  building_elements_with_geometry: number;
  dropped_elements: DroppedElement[];
  next_steps: string[];
}

type Diagnostics = ExportDiagnostics | IfcDiagnostics;

function isIfcDiagnostics(diagnostics: Diagnostics): diagnostics is IfcDiagnostics {
  return diagnostics.format === 'ifc';
}

/// Builds the same report shape `rvt-inspect` prints for `.ifc` inputs
/// (`IfcInspectReport`), from the already-decoded model plus the
/// dropped-element report `openIfcBytesWithDiagnostics` returns
/// alongside it — `building_elements`/`building_elements_with_geometry`
/// are still plain counts over `model.entities` (the dropped elements
/// ARE in `model.entities` now, with `solid_shape: null`, since
/// PER-1270 GRAVE 2 stopped the reader from silently omitting them).
/// The same `kind === 'BuildingElement'` filter the status panel uses
/// (`buildingElements` below, and `validatedElements`'s IFC counterpart)
/// — `model.entities` also carries scaffold nodes (`IfcProject`,
/// `IfcBuildingElementType`) that aren't construction elements. Shared
/// so the footer's "N elements" can never disagree with the panel's "N
/// building elements decoded" (PER-1273 item 2).
function countBuildingElements(model: IfcModel): number {
  return (model.entities ?? []).filter((e) => e.kind === 'BuildingElement').length;
}

function ifcDiagnosticsFromModel(model: IfcModel, droppedElements: DroppedElement[]): IfcDiagnostics {
  const buildingElements = (model.entities ?? []).filter((e) => e.kind === 'BuildingElement');
  const buildingElementsWithGeometry = buildingElements.filter((e) => e.solid_shape != null);
  const droppedSteps = droppedElements.map(
    (d) => `${d.ifc_type} #${d.express_id} has no geometry: ${d.reason}.`,
  );
  return {
    format: 'ifc',
    schema_version: model.description ?? null,
    building_elements: buildingElements.length,
    building_elements_with_geometry: buildingElementsWithGeometry.length,
    dropped_elements: droppedElements,
    next_steps: [
      ...droppedSteps,
      'Spatial containment (storeys), property sets, and materials are not read from ' +
        '.ifc yet — every element reports outside any storey.',
    ],
  };
}

let model: IfcModel | null = null;
let sceneGraph: SceneNode | null = null;
let distinctTypes: string[] = [];
let lastGlb: Uint8Array | null = null;
let lastFileStem = 'model';
// Populated by `applySupportedProfileToUi` from the same
// `acceptedExtensions` wasm binding the format gate uses — kept here so
// `stripSupportedExtension` doesn't hand-write a fifth copy of the
// extension list next to the one `loadBytes`'s gate already checked.
let cachedAcceptedExtensions: string[] = [];
function stripSupportedExtension(filename: string): string {
  if (cachedAcceptedExtensions.length > 0) {
    const pattern = new RegExp(`\\.(${cachedAcceptedExtensions.join('|')})$`, 'i');
    return filename.replace(pattern, '');
  }
  return filename.replace(/\.[^./\\]+$/, '');
}
let currentDiagnostics: Diagnostics | null = null;
const hiddenTypes = new Set<string>();
// Decoded from `location.hash` at page load (VW1-24 share-link
// flow); applied once the first file finishes loading, then
// cleared. `null` once consumed or if there was nothing to restore.
let pendingRestoreState: ViewerState | null = null;
// `checkHashForRestore` decodes the fragment asynchronously (it
// waits on `ensureMainThreadWasm()`), so it can resolve *after* the
// first file has already finished loading without a restore (PER-1209
// finding 7). Once that happens the decoded state is stale — it was
// meant for the load that already completed, not for whatever file
// gets dropped next — so it must not be adopted late.
let restoreEligible = true;

// Reset every panel that `loadBytes` populates on success, plus the
// internal state backing them. Used when a new file starts loading (so
// the previous file's tree/categories/schedule/info don't linger while
// the next one parses), when a file fails to parse, and when a file is
// rejected by the format gate before any parsing starts (PER-1180
// finding 3: after a panic — and, in PER-1193, after a rejected file —
// the scene tree, categories, schedule, and element info kept showing
// the *previous* file's data under the new file's name in the header;
// there was no reset on either non-success path).
function clearLoadedFileState(): void {
  model = null;
  sceneGraph = null;
  distinctTypes = [];
  lastGlb = null;
  currentDiagnostics = null;
  hiddenTypes.clear();
  exportGlbBtn.disabled = true;
  exportIfcBtn.disabled = true;
  exportSvgBtn.disabled = true;
  shareLinkBtn.disabled = true;
  downloadDiagnosticsBtn.disabled = true;
  exportQualityEl.textContent = 'quality: pending';
  exportQualityEl.className = 'quality-pill';
  diagnosticsJsonEl.textContent = '';
  treeEl.innerHTML = '';
  categoriesEl.innerHTML = '';
  scheduleEl.textContent = '(empty)';
  infoEl.textContent = '';
  // Streamed chunks (and any LOD proxy) are the only live RVT/IFC
  // geometry objects now — `chunkManager.disposeAll()` removes them
  // from `scene` and disposes their geometry/materials. The DXF
  // underlay group is separate (never goes through the chunk
  // manager) and needs its own disposal.
  chunkManager.disposeAll();
  manifestLoaded = false;
  disposeDxfUnderlayGroup();
}

// ---------- Load flow ----------
// PER-1248 — no message from the parse worker is guaranteed to ever
// arrive: a real 16 MB Revit 2026 project regressed to a screen that
// sat on "Not parsed yet" indefinitely, with nothing to tell the user
// whether it was still working or dead. 90s is generous headroom over
// the ~5-6s this file takes end-to-end in a real browser (measured via
// Playwright against this exact fixture) — long enough that ordinary
// large files don't trip it, short enough that a genuine stall
// resolves to an explicit error instead of staying pending forever.
const DEFAULT_PARSE_TIMEOUT_MS = 90_000;
// PER-1254 médio 2 — the watchdog path had no way to verify itself
// except editing this constant by hand, which is a path with no
// regression protection. `?parseTimeoutMs=` lets Playwright trigger the
// real timeout state (instead of waiting 90s or hand-patching source)
// without changing production behavior: absent or invalid, it falls
// back to the same 90s budget as before.
function resolveParseTimeoutMs(): number {
  const override = new URLSearchParams(window.location.search).get('parseTimeoutMs');
  const parsed = override === null ? NaN : Number(override);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PARSE_TIMEOUT_MS;
}
const PARSE_TIMEOUT_MS = resolveParseTimeoutMs();

// PER-1254 — identifies which `loadBytes()` invocation a load's
// ticker/watchdog/message-handler belong to. Opening a second file
// while a first is still in flight used to leave the first load's
// ticker and watchdog running forever: `resetWorker()` kills the old
// Worker, so the old invocation's `settle()` never runs (it only runs
// on that invocation's own 'ready'/'error' message), and nothing else
// stopped its timers. The result was the abandoned load's panel
// overwriting the new file's panel, and — 90s later — its watchdog
// tearing down the model the *second* file had already loaded. Every
// callback below compares its captured `myGeneration` against this
// counter and treats a mismatch as "superseded, become a no-op".
let loadGeneration = 0;

async function loadBytes(file: File): Promise<void> {
  // The format gate runs before anything else in this function commits
  // to loading `file` — including the state reset below — so a rejected
  // file can never leave a previously-loaded file's scene tree,
  // categories, schedule, element info, header, or enabled export
  // buttons on screen next to a "not supported" status (PER-1193
  // blocker 2: this used to return here *before* the reset that now
  // runs unconditionally right after).
  let classifyFilename: (name: string) => unknown;
  try {
    ({ classifyFilename } = await ensureMainThreadWasm());
  } catch (err) {
    clearLoadedFileState();
    fileMetaEl.textContent = '';
    setStatus(`failed to initialize: ${(err as Error).message ?? err}`);
    renderUnsupportedFormatStatusPanel(
      'The viewer failed to initialize. Reload the page and try again.',
    );
    dropzone.classList.remove('hidden');
    return;
  }
  const classification = classifyFilename(file.name) as { supported: boolean; message?: string };
  if (!classification.supported) {
    const message = classification.message ?? `${file.name} is not supported.`;
    clearLoadedFileState();
    fileMetaEl.textContent = '';
    setStatus(`ignored: ${file.name}`);
    renderUnsupportedFormatStatusPanel(message);
    dropzone.classList.remove('hidden');
    return;
  }

  // PER-1220 — DXF is not a Revit/CFB container and never produces an
  // IfcModel/glb/scene-graph: it is a 2D reference underlay, a
  // deliberately separate and much smaller pipeline than the worker
  // flow below. Branch out before any of that RVT-specific state
  // machinery runs.
  if (file.name.toLowerCase().endsWith('.dxf')) {
    await loadDxfUnderlay(file);
    return;
  }

  setStatus(`reading ${formatBytes(file.size)}…`);
  clearLoadedFileState();
  fileMetaEl.textContent = '';
  renderLoadingStatusPanel(file.name);
  const bytes = new Uint8Array(await file.arrayBuffer());

  // Set once the worker's 'summary' message proves the CFB/OLE header
  // was actually read. An error that arrives *after* this point is not
  // "corrupt or unreadable file" — the container opened fine and a
  // later decode step failed instead. Conflating the two was PER-1180
  // finding 2: a real 2024 project file that fails mid-parse got
  // labelled "not a readable Revit OLE/CFB container" right next to a
  // header the same screen had just printed from that same container.
  let headerParsed = false;

  // PER-1248 — live loading state, refreshed on every worker progress
  // tick AND on a fixed interval (below), so the panel keeps moving
  // even during the single long synchronous decode step that emits no
  // intermediate progress message of its own.
  let lastProgressStep = 'starting';
  let summaryClassCount: number | null = null;
  let lastElementCount: number | null = null;
  let lastTriangleCount: number | null = null;
  const loadStartedAt = performance.now();
  let settled = false;
  // Captured now, synchronously, with no `await` between this line and
  // the `resetWorker()` call below — so no other `loadBytes()` call can
  // interleave and observe a stale `loadGeneration`. Every callback this
  // invocation creates carries this number and treats a mismatch against
  // the (possibly since-bumped) module counter as "a newer load has
  // started, stand down".
  const myGeneration = ++loadGeneration;

  function refreshLoadingPanel(): void {
    renderLoadingStatusPanel(file.name, {
      step: lastProgressStep,
      elapsedMs: performance.now() - loadStartedAt,
      classNameCount: summaryClassCount,
      elementCount: lastElementCount,
      triangleCount: lastTriangleCount,
    });
  }

  const loadingTicker = window.setInterval(() => {
    if (myGeneration !== loadGeneration) {
      window.clearInterval(loadingTicker);
      return;
    }
    refreshLoadingPanel();
  }, 500);

  function settle(): void {
    if (settled) return;
    settled = true;
    window.clearInterval(loadingTicker);
    window.clearTimeout(watchdog);
  }

  const watchdog = window.setTimeout(() => {
    if (settled || myGeneration !== loadGeneration) return;
    settle();
    setStatus(`error: parse exceeded ${(PARSE_TIMEOUT_MS / 1000).toFixed(0)}s time budget`);
    renderTimeoutStatusPanel(file.name, PARSE_TIMEOUT_MS, headerParsed, summaryClassCount);
    clearLoadedFileState();
    dropzone.classList.remove('hidden');
    worker?.terminate();
    worker = null;
  }, PARSE_TIMEOUT_MS);

  const w = resetWorker();
  w.addEventListener('message', (ev: MessageEvent<unknown>) => {
    void handleWorkerMessage(ev);
  });
  async function handleWorkerMessage(ev: MessageEvent<unknown>): Promise<void> {
    // A message already queued from this generation's Worker at the
    // moment a newer `loadBytes()` call terminated it (PER-1254) must
    // not paint over the newer load's panel — that check applies to
    // every message type, `chunkReady` included.
    if (myGeneration !== loadGeneration) return;
    const msg = ev.data as
      | {
          type: 'progress';
          step: string;
          memoryBytes?: number | null;
          memoryMetric?: string | null;
          ts?: number;
          fileBytes?: number;
          glbBytes?: number;
          elementCount?: number | null;
          triangleCount?: number | null;
        }
      | {
          type: 'summary';
          summary: { version: number; build?: string; guid?: string; class_name_count?: number };
        }
      | {
          type: 'ready';
          model: IfcModel;
          scene: SceneNode;
          types: string[];
          glb: Uint8Array;
          schedule: unknown;
          format: 'rvt' | 'ifc';
          diagnostics: ExportDiagnostics | null;
          droppedElements: DroppedElement[];
          manifest: ChunkManifestEntry[];
        }
      | { type: 'chunkReady'; chunkId: number; glb: Uint8Array }
      | { type: 'error'; message: string };
    // `chunkReady` messages keep arriving for the entire life of the
    // loaded file — chunks stream in on demand as the camera moves,
    // long after the initial parse has `settle()`d. Gating this branch
    // on `settled` (as the other branches below correctly are) meant
    // every chunk that streamed in after the "ready" message — which is
    // effectively all of them, since chunks are requested lazily from
    // `tick()` — was silently dropped and never reached the scene. The
    // watchdog only terminates the worker (`worker = null`) on timeout,
    // so a dead worker cannot produce a `chunkReady` here regardless of
    // this flag; `settled` is not needed to protect this branch.
    if (msg.type === 'chunkReady') {
      chunkManager.onChunkBytes(msg.chunkId, msg.glb);
      // A chunk can stream in after the user has already hidden a
      // category (e.g. from a restored share-link state, or a
      // checkbox toggled while other chunks were still in flight) —
      // without this, a freshly-arrived chunk would ignore the
      // current filter until the next unrelated visibility trigger.
      applyCategoryVisibility();
      return;
    }
    if (settled) return;
    if (msg.type === 'progress') {
      setStatus(msg.step);
      lastProgressStep = msg.step;
      if (msg.elementCount != null) lastElementCount = msg.elementCount;
      if (msg.triangleCount != null) lastTriangleCount = msg.triangleCount;
      refreshLoadingPanel();
      logMemProfileCheckpoint({
        step: msg.step,
        memoryBytes: msg.memoryBytes ?? null,
        memoryMetric: msg.memoryMetric ?? null,
        ts: msg.ts ?? null,
        fileBytes: msg.fileBytes ?? null,
        glbBytes: msg.glbBytes ?? null,
        elementCount: msg.elementCount ?? null,
        triangleCount: msg.triangleCount ?? null,
      });
      return;
    }
    if (msg.type === 'summary') {
      // VW1-20 — show the fast metadata the moment the worker has
      // cracked BasicFileInfo, before the full parse finishes.
      const bits = [
        `${file.name}`,
        formatBytes(file.size),
        `Revit ${msg.summary.version}`,
      ];
      if (msg.summary.build) bits.push(msg.summary.build);
      if (msg.summary.class_name_count !== undefined) {
        bits.push(`${msg.summary.class_name_count} classes`);
      }
      fileMetaEl.textContent = bits.join(' · ');
      dropzone.classList.add('hidden');
      headerParsed = true;
      // PER-1248 — the header above and the FILE STATUS panel's Schema
      // row must agree the moment this number is known. Feeding it
      // into the same `summaryClassCount` the ticker/progress refresh
      // reads is what keeps them a single source of truth instead of
      // two clocks that can disagree for the rest of the load.
      summaryClassCount = msg.summary.class_name_count ?? null;
      refreshLoadingPanel();
      return;
    }
    if (msg.type === 'error') {
      settle();
      setStatus(`error: ${msg.message}`);
      renderErrorStatusPanel(msg.message, headerParsed);
      clearLoadedFileState();
      dropzone.classList.remove('hidden');
      return;
    }
    settle();
    // PER-1248 followup — "quanto tempo leva em WASM? Meça." The final
    // panel states this number instead of leaving completion vs. "still
    // working" to be inferred from the absence of a spinner.
    const parseElapsedMs = performance.now() - loadStartedAt;
    model = msg.model;
    sceneGraph = msg.scene;
    distinctTypes = msg.types;
    lastGlb = msg.glb;
    const diagnostics: Diagnostics =
      msg.format === 'ifc'
        ? ifcDiagnosticsFromModel(msg.model, msg.droppedElements)
        : (msg.diagnostics as ExportDiagnostics);
    currentDiagnostics = diagnostics;
    lastFileStem = stripSupportedExtension(file.name);
    // PER-1218 phase 2 — the monolithic `glb` above is kept ONLY for
    // the "Export GLB" button (`lastGlb`); it is deliberately never
    // added to the live scene anymore (that would defeat the whole
    // point of on-demand streaming + eviction). The chunk manager
    // owns everything that actually renders from here on: `update()`
    // (called from `tick()`) requests each visible chunk from the
    // manifest and adds it to `scene` once its bytes arrive.
    chunkManager.setManifest(msg.manifest);
    manifestLoaded = true;
    if (!pendingRestoreState) {
      const bounds = chunkManager.manifestBounds();
      if (bounds) frameCameraFromBounds(bounds.min, bounds.max);
    }
    // PER-1191 — main-thread checkpoint after the three.js scene (the
    // fourth live copy of the model, alongside bytes/IR/GLB) is
    // populated. Awaited so the checkpoint is emitted before the
    // 'loaded' status flip the spec waits on.
    const { bytes: memoryBytes, metric: memoryMetric } = await measureMainThreadMemory();
    const checkpoint: MemProfileCheckpoint = {
      step: 'three.js scene populated',
      memoryBytes,
      memoryMetric,
      ts: performance.now(),
      fileBytes: null,
      glbBytes: msg.glb.byteLength,
      // PER-1197 médio 3 — `elementCount` must mean the same thing on
      // every row: `model.entities.length`, the decoded-IR entity
      // count worker.ts reports on every earlier checkpoint. The
      // scene-graph node walk (`countEntities`, used below only for
      // the human-facing "N entities" label) is a different number —
      // it can disagree with the IR count, and mixing the two into
      // one column defeats the "does the curve track element count?"
      // question this column exists to answer.
      elementCount: msg.model.entities?.length ?? null,
      triangleCount: countGlbTriangles(msg.glb),
    };
    logMemProfileCheckpoint(checkpoint);
    if (pendingRestoreState) {
      const restored = pendingRestoreState;
      pendingRestoreState = null;
      applyRestoredState(restored);
    } else {
      renderCategories();
      void refreshTree();
    }
    // A fragment that decodes after this point belongs to a load
    // that already finished — see the `restoreEligible` comment.
    restoreEligible = false;
    renderScheduleSummary(msg.schedule);
    renderExportQuality(diagnostics);
    renderStatusPanel(diagnostics, parseElapsedMs);
    fileMetaEl.textContent = `${file.name} · ${formatBytes(file.size)} · ${countEntities(msg.scene)} entities`;
    dropzone.classList.add('hidden');
    exportGlbBtn.disabled = false;
    exportIfcBtn.disabled = false;
    exportSvgBtn.disabled = false;
    shareLinkBtn.disabled = false;
    downloadDiagnosticsBtn.disabled = false;
    // PER-1270: `msg.types.length` counts distinct IFC types in the scene
    // TREE, which always includes the synthetic root `IFCPROJECT` node —
    // so "1 categories" alone reads as "loaded something" even when zero
    // real building elements decoded (the scaffold-only case). Leading
    // with the actual decoded-element count keeps this line honest and
    // in agreement with the status panel's "N building elements decoded".
    //
    // PER-1273 item 2: that decoded-element count must be
    // `countBuildingElements`, not `checkpoint.elementCount`
    // (`model.entities.length`) — the latter also counts the
    // `IfcProject` scaffold node every model carries at entities[0]
    // (`src/ifc/mod.rs`), so a scaffold-only file previously read
    // "1 elements" instead of "0 elements" here. The status panel's
    // "Elements" row already counts the same `kind === 'BuildingElement'`
    // set; this keeps the two in agreement instead of just relabeling
    // the same wrong number.
    setStatus(`loaded · ${countBuildingElements(msg.model)} elements · ${msg.types.length} categories`);
  }
  w.postMessage({ type: 'parse', bytes, filename: file.name }, [bytes.buffer]);
}

// ---------- DXF underlay (PER-1220) ----------
// `import dxf --as-underlay`, browser side. Deliberately not routed
// through the RVT worker/glb/scene-graph pipeline above: a DXF underlay
// is 2D reference geometry, never an IfcModel, so it gets its own much
// smaller path — parsed on the main thread (DXF files are small enough
// that a worker hop buys nothing) and drawn as plain line segments.
// Export/schedule/share buttons stay disabled (`clearLoadedFileState`
// already does that) because none of those apply to an underlay.
type DxfUnderlayGeometry =
  | { kind: 'Line'; p1: [number, number]; p2: [number, number] }
  | { kind: 'Polyline'; points: [number, number][]; closed: boolean }
  | { kind: 'Circle'; center: [number, number]; radius: number }
  | { kind: 'Arc'; center: [number, number]; radius: number; start_angle: number; end_angle: number }
  | { kind: 'Text'; position: [number, number]; value: string; height: number };

interface DxfUnderlayEntity {
  layer: string;
  geometry: DxfUnderlayGeometry;
}

interface DxfUnderlay {
  entities: DxfUnderlayEntity[];
  layers: string[];
  unit: unknown;
  unit_source: 'DxfHeaderInsUnits' | 'DefaultedNoDeclaredUnit';
  converted_entity_counts: Record<string, number>;
  parsed_but_unconverted_counts: Record<string, number>;
  unrecognized_entity_counts: Record<string, number> | null;
  known_gaps: string[];
}

/// Converts a DXF underlay into flat line-segment positions for a single
/// `THREE.LineSegments`. Circles/arcs are tessellated; `Text` entities
/// are counted in the diagnostics panel but not drawn as 3-D glyphs —
/// this is a reference underlay, not a text-rendering engine.
function dxfUnderlayToLineSegments(underlay: DxfUnderlay): THREE.LineSegments {
  const positions: number[] = [];
  const pushSegment = (a: [number, number], b: [number, number]): void => {
    positions.push(a[0], a[1], 0, b[0], b[1], 0);
  };
  const pushArc = (
    center: [number, number],
    radius: number,
    startDeg: number,
    endDeg: number,
  ): void => {
    const steps = 48;
    const startRad = (startDeg * Math.PI) / 180;
    let span = ((endDeg - startDeg) * Math.PI) / 180;
    if (span <= 0) span += 2 * Math.PI;
    let prev: [number, number] | null = null;
    for (let i = 0; i <= steps; i++) {
      const t = startRad + (span * i) / steps;
      const p: [number, number] = [
        center[0] + radius * Math.cos(t),
        center[1] + radius * Math.sin(t),
      ];
      if (prev) pushSegment(prev, p);
      prev = p;
    }
  };
  for (const entity of underlay.entities) {
    const g = entity.geometry;
    switch (g.kind) {
      case 'Line':
        pushSegment(g.p1, g.p2);
        break;
      case 'Polyline':
        for (let i = 0; i + 1 < g.points.length; i++) pushSegment(g.points[i], g.points[i + 1]);
        if (g.closed && g.points.length > 1) {
          pushSegment(g.points[g.points.length - 1], g.points[0]);
        }
        break;
      case 'Circle':
        pushArc(g.center, g.radius, 0, 360);
        break;
      case 'Arc':
        pushArc(g.center, g.radius, g.start_angle, g.end_angle);
        break;
      case 'Text':
        break;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const material = new THREE.LineBasicMaterial({ color: 0x4fc3f7 });
  return new THREE.LineSegments(geometry, material);
}

/// Mirrors `UnitSource::describe()` in `src/dxf_underlay.rs` — the enum
/// serializes as its bare variant name, so the user-facing sentence has
/// to be reconstructed here rather than read off the wire.
function describeUnitSource(source: DxfUnderlay['unit_source']): string {
  switch (source) {
    case 'DxfHeaderInsUnits':
      return 'read from the DXF $INSUNITS header variable';
    case 'DefaultedNoDeclaredUnit':
      return "the file declared no unit ($INSUNITS was absent or unitless); defaulted to " +
        'millimeters — verify scale before trusting drawing dimensions';
    default:
      return 'unknown source';
  }
}

function renderDxfUnderlayStatusPanel(file: File, underlay: DxfUnderlay): void {
  statusPanelEl.innerHTML = '';
  statusPanelEl.appendChild(statusRow('File', 'ok', `${file.name} · underlay loaded`));
  statusPanelEl.appendChild(
    statusRow('Mode', 'warn', 'as-underlay · 2D reference, layers not interpreted as elements'),
  );
  const unitLabel = typeof underlay.unit === 'string' ? underlay.unit : JSON.stringify(underlay.unit);
  const unitAssumed = underlay.unit_source === 'DefaultedNoDeclaredUnit';
  statusPanelEl.appendChild(
    statusRow(
      'Unit',
      unitAssumed ? 'warn' : 'ok',
      `${unitLabel} (${describeUnitSource(underlay.unit_source)})`,
    ),
  );
  const convertedTotal = Object.values(underlay.converted_entity_counts).reduce(
    (a, b) => a + b,
    0,
  );
  statusPanelEl.appendChild(
    statusRow('Entities', 'ok', `${convertedTotal} drawn across ${underlay.layers.length} layers`),
  );
  const unrecognizedTotal = underlay.unrecognized_entity_counts
    ? Object.values(underlay.unrecognized_entity_counts).reduce((a, b) => a + b, 0)
    : null;
  if (unrecognizedTotal !== null && unrecognizedTotal > 0) {
    statusPanelEl.appendChild(
      statusRow('Unrecognized', 'bad', `${unrecognizedTotal} entities dropped by the parser`),
    );
  } else if (unrecognizedTotal === null) {
    statusPanelEl.appendChild(
      statusRow('Unrecognized', 'warn', 'not checked (binary DXF)'),
    );
  } else {
    statusPanelEl.appendChild(statusRow('Unrecognized', 'ok', 'none'));
  }
  statusPanelEl.appendChild(
    statusRow('Gaps', underlay.known_gaps.length > 0 ? 'warn' : 'ok', underlay.known_gaps.join(' ')),
  );
  diagnosticsJsonEl.textContent = JSON.stringify(underlay, null, 2);
}

async function loadDxfUnderlay(file: File): Promise<void> {
  setStatus(`reading ${formatBytes(file.size)}…`);
  clearLoadedFileState();
  fileMetaEl.textContent = '';
  renderLoadingStatusPanel(file.name);
  const bytes = new Uint8Array(await file.arrayBuffer());

  let underlay: DxfUnderlay;
  try {
    const { openDxfUnderlayBytes } = await ensureMainThreadWasm();
    underlay = openDxfUnderlayBytes(bytes) as unknown as DxfUnderlay;
  } catch (err) {
    const message = (err as Error).message ?? String(err);
    setStatus(`error: ${message}`);
    renderErrorStatusPanel(message, false);
    clearLoadedFileState();
    dropzone.classList.remove('hidden');
    return;
  }

  dxfUnderlayGroup = new THREE.Group();
  dxfUnderlayGroup.add(dxfUnderlayToLineSegments(underlay));
  scene.add(dxfUnderlayGroup);
  frameCamera(dxfUnderlayGroup);

  fileMetaEl.textContent = `${file.name} · ${formatBytes(file.size)} · ${underlay.entities.length} entities · ${underlay.layers.length} layers`;
  dropzone.classList.add('hidden');
  renderDxfUnderlayStatusPanel(file, underlay);
  setStatus(`loaded · DXF underlay · ${underlay.layers.length} layers`);
}

function renderEmptyStatusPanel(): void {
  statusPanelEl.innerHTML = '';
  statusPanelEl.appendChild(statusRow('File', 'warn', 'No file opened'));
  statusPanelEl.appendChild(statusRow('Mode', 'warn', 'Waiting for file'));
  statusPanelEl.appendChild(statusRow('Schema', 'warn', 'Waiting for file'));
  statusPanelEl.appendChild(statusRow('Elements', 'warn', 'Waiting for file'));
  statusPanelEl.appendChild(statusRow('Geometry', 'warn', 'Waiting for file'));
  statusPanelEl.appendChild(statusRow('IFC', 'warn', 'Waiting for file'));
  statusPanelEl.appendChild(statusRow('Warnings', 'ok', 'No export warnings'));
  diagnosticsJsonEl.textContent = '';
}

// PER-1248 — a single frozen snapshot rendered once at load start and
// never touched again until 'ready'/'error' produced two visible bugs:
// the panel kept saying "Not parsed yet" long after the header (fed by
// the worker's separate 'summary' message) already showed a real class
// count, and nothing on screen moved during the — sometimes long —
// single-threaded decode, which is indistinguishable from a hang. This
// is re-invoked on every worker 'progress' message and every tick of
// the elapsed-time interval below, so it always reflects what the
// worker has actually told us, not what we assumed at t=0.
// PER-1248 followup — the worker (worker.ts) already knows which phase
// it's in; these are its exact `progress('...')` step strings, in
// pipeline order. Reusing them (instead of re-deriving a parallel
// notion of "phase") keeps this list from silently drifting out of
// sync with what the worker actually emits.
const PHASE_STEPS = [
  'initializing wasm',
  'reading file metadata',
  'parsing container',
  'parsed container (IR built)',
  'building scene graph',
  'scene graph built',
  'rendering glTF',
  'glTF built',
  'building schedule',
] as const;

function phaseIndex(step: string): number {
  const i = PHASE_STEPS.indexOf(step as (typeof PHASE_STEPS)[number]);
  return i === -1 ? -1 : i;
}
const PHASE_ELEMENTS_DECODED = phaseIndex('parsed container (IR built)');
const PHASE_GEOMETRY_RENDERED = phaseIndex('glTF built');
const PHASE_SCHEDULE_STARTED = phaseIndex('building schedule');

function renderLoadingStatusPanel(
  filename: string,
  progress: {
    step: string;
    elapsedMs: number;
    classNameCount: number | null;
    elementCount: number | null;
    triangleCount: number | null;
  } = {
    step: 'starting',
    elapsedMs: 0,
    classNameCount: null,
    elementCount: null,
    triangleCount: null,
  },
): void {
  statusPanelEl.innerHTML = '';
  const elapsed = `${(progress.elapsedMs / 1000).toFixed(1)}s elapsed`;
  statusPanelEl.appendChild(statusRow('File', 'warn', `Reading ${filename} · ${elapsed}`));
  statusPanelEl.appendChild(statusRow('Mode', 'warn', progress.step));
  if (progress.classNameCount !== null) {
    statusPanelEl.appendChild(
      statusRow('Schema', 'ok', `Schema parsed · ${progress.classNameCount} classes`),
    );
  } else {
    statusPanelEl.appendChild(statusRow('Schema', 'warn', 'Not parsed yet'));
  }
  // PER-1248 comment — "each panel line resolves when that phase
  // resolves, not all at the end". The final validated-element /
  // export-quality numbers are only known once the whole diagnostics
  // pipeline finishes (they're an aggregate, not decoded per-element),
  // so these interim rows are deliberately worded "so far" /
  // "validating…" rather than claiming the final count early — that
  // would just move the "painel mentindo" defect to a different row.
  const phase = phaseIndex(progress.step);
  statusPanelEl.appendChild(
    phase >= PHASE_ELEMENTS_DECODED && progress.elementCount !== null
      ? statusRow('Elements', 'warn', `${progress.elementCount} raw entities decoded · validating…`)
      : statusRow('Elements', 'warn', 'Not decoded yet'),
  );
  statusPanelEl.appendChild(
    phase >= PHASE_GEOMETRY_RENDERED && progress.triangleCount !== null
      ? statusRow('Geometry', 'warn', `Geometry rendered · ${progress.triangleCount} triangles · finalizing…`)
      : statusRow('Geometry', 'warn', 'Not decoded yet'),
  );
  statusPanelEl.appendChild(
    phase >= PHASE_SCHEDULE_STARTED
      ? statusRow('IFC', 'warn', 'Computing export readiness…')
      : statusRow('IFC', 'warn', 'Not evaluated yet'),
  );
  statusPanelEl.appendChild(statusRow('Warnings', 'ok', 'No export warnings'));
}

// PER-1248 — a client-side watchdog fires this when the worker hasn't
// answered ('ready' or 'error') within PARSE_TIMEOUT_MS. Distinct from
// `renderErrorStatusPanel`: the worker itself hasn't reported a
// failure — it may still be running — so this must not claim "decoder
// error", only that the time budget ran out. The acceptance bar here
// is "never pendente eterno": some explicit end state beats a screen
// that looks alive forever.
function renderTimeoutStatusPanel(
  filename: string,
  timeoutMs: number,
  headerParsed: boolean,
  classNameCount: number | null = null,
): void {
  statusPanelEl.innerHTML = '';
  statusPanelEl.appendChild(
    statusRow('File', 'bad', `${filename} · exceeded ${(timeoutMs / 1000).toFixed(0)}s time budget`),
  );
  statusPanelEl.appendChild(
    statusRow(
      'Mode',
      'bad',
      headerParsed
        ? 'Stalled after opening the container · decode never finished'
        : 'Stalled before the container header was read',
    ),
  );
  // PER-1254 médio 3 — the header above this panel may already be
  // showing a real class count (the worker's 'summary' message arrives
  // long before decode finishes). Saying "Unknown" here contradicts
  // that header on the same screen — the exact "panel disagrees with
  // itself" defect PER-1248 was filed over, just relocated to the
  // timeout state instead of the loading state.
  statusPanelEl.appendChild(
    classNameCount !== null
      ? statusRow('Schema', 'ok', `Schema parsed · ${classNameCount} classes`)
      : statusRow('Schema', 'warn', 'Unknown — parse did not complete'),
  );
  statusPanelEl.appendChild(statusRow('Elements', 'warn', 'Unknown — parse did not complete'));
  statusPanelEl.appendChild(statusRow('Geometry', 'warn', 'Unknown — parse did not complete'));
  statusPanelEl.appendChild(statusRow('IFC', 'warn', 'Unknown — parse did not complete'));
  statusPanelEl.appendChild(
    statusRow('Warnings', 'bad', 'Parse exceeded its time budget and was abandoned'),
  );
  diagnosticsJsonEl.textContent = '';
}

function renderErrorStatusPanel(message: string, headerParsed: boolean): void {
  statusPanelEl.innerHTML = '';
  if (headerParsed) {
    // The worker already sent a 'summary' before this error, which
    // means it read a valid CFB/OLE container and Revit header — the
    // failure happened later, decoding the model. Calling the file
    // "corrupt or unreadable" here would contradict the header this
    // same screen showed moments earlier.
    statusPanelEl.appendChild(statusRow('File', 'bad', 'Opened, then failed to decode'));
    statusPanelEl.appendChild(
      statusRow('Mode', 'bad', 'Decoder error · the container and header are valid'),
    );
  } else {
    statusPanelEl.appendChild(statusRow('File', 'bad', 'Could not open file'));
    statusPanelEl.appendChild(
      statusRow(
        'Mode',
        'bad',
        'Corrupt or unreadable file · not a readable Revit OLE/CFB container',
      ),
    );
  }
  statusPanelEl.appendChild(statusRow('Schema', 'warn', 'Not parsed'));
  statusPanelEl.appendChild(statusRow('Elements', 'warn', 'Not decoded'));
  statusPanelEl.appendChild(statusRow('Geometry', 'warn', 'Not decoded'));
  statusPanelEl.appendChild(statusRow('IFC', 'warn', 'Not available'));
  statusPanelEl.appendChild(statusRow('Warnings', 'bad', message));
  diagnosticsJsonEl.textContent = '';
}

/// Distinct from `renderErrorStatusPanel`: this is for a file rejected
/// by name *before* any byte was read (wrong format), never for a
/// supported-extension file that failed to open. Confusing the two
/// would call a format-boundary decision a corrupt-file defect.
function renderUnsupportedFormatStatusPanel(message: string): void {
  statusPanelEl.innerHTML = '';
  statusPanelEl.appendChild(statusRow('File', 'bad', 'Unsupported file format'));
  statusPanelEl.appendChild(statusRow('Warnings', 'bad', message));
  diagnosticsJsonEl.textContent = '';
}

// PER-1218 phase 2 — replaces the old `renderScene`/`frameCamera(obj)`
// pair. There is no longer a single monolithic `gltf.scene` added to
// the live scene to frame from (see the `ready` handler above): the
// chunk manager streams geometry in on demand, so the only thing
// available immediately on load is the manifest's AABB union — cheap,
// synchronous, and doesn't need to wait for a single chunk to fetch.
function frameCameraFromBounds(
  min: [number, number, number],
  max: [number, number, number],
): void {
  const box = new THREE.Box3(
    new THREE.Vector3(min[0], min[1], min[2]),
    new THREE.Vector3(max[0], max[1], max[2]),
  );
  if (box.isEmpty()) return;
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  const dist = Math.abs(maxDim / Math.sin(fov / 2)) * 0.8;
  camera.position.copy(center).add(new THREE.Vector3(1, 0.8, 1).normalize().multiplyScalar(dist));
  controls.target.copy(center);
  camera.near = maxDim / 100;
  camera.far = dist * 10;
  camera.updateProjectionMatrix();
}

// ---------- Panels ----------
// Re-render the tree panel restricted to `hiddenTypes` (the same
// checkboxes that drive 3-D visibility). Delegates the pruning to
// the wasm `applyCategoryFilter` — the source-of-truth CategoryFilter
// logic `distinctIfcTypes`'s sibling export — instead of
// reimplementing "walk the tree, skip a type" here in TypeScript.
async function refreshTree(): Promise<void> {
  if (!sceneGraph) return;
  let visible = sceneGraph;
  if (hiddenTypes.size > 0) {
    const { applyCategoryFilter } = await ensureMainThreadWasm();
    const filter = { hidden: Array.from(hiddenTypes) };
    visible = applyCategoryFilter(
      sceneGraph as unknown as object,
      filter as unknown as object,
    ) as unknown as SceneNode;
  }
  treeEl.innerHTML = '';
  treeEl.appendChild(buildTreeNode(visible));
}
function buildTreeNode(node: SceneNode): HTMLElement {
  const wrap = document.createElement('div');
  const row = document.createElement('div');
  row.className = 'tree-node';
  row.textContent = `${node.name} · ${node.ifc_type}`;
  row.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (node.entity_index !== null) showElementInfo(node.entity_index);
  });
  wrap.appendChild(row);
  if (node.children.length > 0) {
    const ch = document.createElement('div');
    ch.className = 'tree-children';
    for (const c of node.children) ch.appendChild(buildTreeNode(c));
    wrap.appendChild(ch);
  }
  return wrap;
}

function renderCategories(): void {
  categoriesEl.innerHTML = '';
  for (const t of distinctTypes) {
    const row = document.createElement('label');
    row.className = 'category-toggle';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = !hiddenTypes.has(t);
    cb.addEventListener('change', () => {
      if (cb.checked) hiddenTypes.delete(t);
      else hiddenTypes.add(t);
      applyCategoryVisibility();
      void refreshTree();
    });
    row.appendChild(cb);
    row.append(` ${t}`);
    categoriesEl.appendChild(row);
  }
}

// PER-1218 phase 2 — geometry now arrives as separate per-chunk roots
// added directly to `scene` (see chunk-manager.ts), not one
// `currentModel` group, so this walks the whole scene instead. Grid,
// axes, lights, and LOD proxy boxes never carry `userData.ifcType`
// and are left untouched.
function applyCategoryVisibility(): void {
  scene.traverse((obj) => {
    const u = obj.userData as { ifcType?: string };
    if (u.ifcType) obj.visible = !hiddenTypes.has(u.ifcType);
  });
}

function showElementInfo(idx: number): void {
  if (!model) return;
  void (async () => {
    const { elementInfoPanel } = await ensureMainThreadWasm();
    const panel = elementInfoPanel(model as unknown as object, idx) as unknown as
      | ElementInfoPanel
      | null;
    if (!panel) {
      infoEl.textContent = 'No detail panel for this entity (not a building element).';
      return;
    }
    infoEl.innerHTML = '';
    const rows: [string, string][] = [
      ['Name', panel.name],
      ['Type', panel.ifc_type],
      ['Type GUID', panel.type_guid ?? '—'],
      ['Storey', panel.storey_name ?? '—'],
      [
        'Storey elevation',
        panel.storey_elevation_feet != null ? `${panel.storey_elevation_feet.toFixed(2)} ft` : '—',
      ],
      ['Location', panel.location_feet ? panel.location_feet.map((v) => v.toFixed(2)).join(', ') : '—'],
      [
        'Rotation',
        panel.rotation_radians != null ? `${panel.rotation_radians.toFixed(3)} rad` : '—',
      ],
      ['Material', panel.material_name ?? '—'],
      ...panel.properties,
    ];
    for (const [k, v] of rows) {
      const row = document.createElement('div');
      row.className = 'info-row';
      const kE = document.createElement('div');
      kE.className = 'k';
      kE.textContent = k;
      const vE = document.createElement('div');
      vE.className = 'v';
      vE.textContent = v;
      row.appendChild(kE);
      row.appendChild(vE);
      infoEl.appendChild(row);
    }
  })();
}

function renderScheduleSummary(schedule: unknown): void {
  const s = schedule as { rows?: unknown[] } | null;
  if (!s || !s.rows) {
    scheduleEl.textContent = '(empty)';
    return;
  }
  scheduleEl.textContent = `${s.rows.length} scheduled elements`;
}

function renderExportQuality(diagnostics: Diagnostics): void {
  if (isIfcDiagnostics(diagnostics)) {
    renderIfcExportQuality(diagnostics);
    return;
  }
  renderRvtExportQuality(diagnostics);
}

function renderIfcExportQuality(diagnostics: IfcDiagnostics): void {
  // An `.ifc` input has no "export quality" to score — it already is
  // the target format, read with exact geometry provenance (never a
  // `BoundingBoxGuess`; see `src/ifc/reader.rs`). The pill names the
  // source instead of forcing a Revit-shaped confidence score onto a
  // file that never went through that path.
  exportQualityEl.textContent = `IFC source · ${diagnostics.schema_version ?? 'unknown schema'}`;
  exportQualityEl.className = 'quality-pill geometry';
  exportIfcBtn.title = `Download as IFC4 STEP · read from ${diagnostics.schema_version ?? 'unknown schema'} · ${diagnostics.building_elements} elements · ${diagnostics.building_elements_with_geometry} with geometry`;
}

function renderRvtExportQuality(diagnostics: ExportDiagnostics): void {
  const level = diagnostics.confidence?.level ?? 'unknown';
  const label = exportQualityLabel(level);
  const score = diagnostics.confidence?.score;
  const suffix = typeof score === 'number' ? ` · ${Math.round(score * 100)}%` : '';
  exportQualityEl.textContent = `${label}${suffix}`;
  exportQualityEl.className = `quality-pill ${exportQualityClass(level)}`;

  const elements = diagnostics.exported?.building_elements ?? 0;
  const geometry = diagnostics.exported?.building_elements_with_geometry ?? 0;
  const warnings = diagnostics.confidence?.warning_count ?? diagnostics.warnings?.length ?? 0;
  exportIfcBtn.title = `Download as IFC4 STEP · ${label} · ${elements} elements · ${geometry} with geometry · ${warnings} warnings`;
}

function renderStatusPanel(diagnostics: Diagnostics, parseElapsedMs: number | null = null): void {
  if (isIfcDiagnostics(diagnostics)) {
    renderIfcStatusPanel(diagnostics, parseElapsedMs);
    return;
  }
  renderRvtStatusPanel(diagnostics, parseElapsedMs);
}

// PER-1248 followup — every completed-panel File row runs through this
// so "parsed in Xs" always means the same measurement (wall-clock from
// the first byte read to the worker's 'ready' message) regardless of
// which format/panel produced the row. Distinguishing "finished" from
// "still working" was AC 3 of the followup: this text only exists once
// the load is actually done.
function parsedInSuffix(parseElapsedMs: number | null): string {
  return parseElapsedMs === null ? '' : ` · parsed in ${(parseElapsedMs / 1000).toFixed(1)}s`;
}

/// The `.ifc` counterpart to `renderRvtStatusPanel` — mirrors the CLI's
/// `IfcInspectReport` (`src/bin/rvt_inspect.rs`) instead of routing
/// through Revit-shaped fields (`stream_count`, `arcwall_records`, a
/// confidence score) that a STEP text file was never going to
/// populate. A successfully-read `.ifc` file must never show as
/// "Parser bug, please report".
function renderIfcStatusPanel(diagnostics: IfcDiagnostics, parseElapsedMs: number | null = null): void {
  statusPanelEl.innerHTML = '';
  diagnosticsJsonEl.textContent = JSON.stringify(diagnostics, null, 2);

  statusPanelEl.appendChild(
    statusRow('File', 'ok', `Opened · IFC/STEP text file${parsedInSuffix(parseElapsedMs)}`),
  );
  statusPanelEl.appendChild(
    statusRow('Mode', 'ok', 'Native IFC read · geometry is the file’s own profile, not a guess'),
  );
  statusPanelEl.appendChild(
    statusRow('Schema', 'ok', diagnostics.schema_version ?? 'schema version not reported'),
  );
  statusPanelEl.appendChild(
    statusRow('Elements', 'ok', `${diagnostics.building_elements} building elements decoded`),
  );
  // PER-1270 GRAVE 2: green only when every decoded element also has
  // geometry — a partial loss (e.g. one door with no Representation in
  // an otherwise-fine file) must read as a warning, not silently pass
  // as "3 elements have geometry" while a 4th vanished.
  const allElementsHaveGeometry =
    diagnostics.building_elements > 0 &&
    diagnostics.building_elements_with_geometry === diagnostics.building_elements;
  statusPanelEl.appendChild(
    statusRow(
      'Geometry',
      allElementsHaveGeometry ? 'ok' : 'warn',
      `${diagnostics.building_elements_with_geometry} of ${diagnostics.building_elements} elements have geometry`,
    ),
  );
  statusPanelEl.appendChild(statusRow('IFC', 'ok', 'Native IFC input — export quality scoring does not apply'));
  statusPanelEl.appendChild(
    statusRow(
      'Warnings',
      diagnostics.next_steps.length === 0 ? 'ok' : 'warn',
      diagnostics.next_steps.length === 0 ? 'No known gaps' : diagnostics.next_steps.join(' '),
    ),
  );
}

function renderRvtStatusPanel(diagnostics: ExportDiagnostics, parseElapsedMs: number | null = null): void {
  statusPanelEl.innerHTML = '';
  diagnosticsJsonEl.textContent = JSON.stringify(diagnostics, null, 2);

  const input = diagnostics.input ?? {};
  const decoded = diagnostics.decoded ?? {};
  const exported = diagnostics.exported ?? {};
  const confidence = diagnostics.confidence ?? {};
  const warnings = diagnostics.warnings ?? [];
  const unsupported = diagnostics.unsupported_features ?? [];
  const validatedElements =
    (decoded.production_walker_elements ?? 0) + (decoded.arcwall_records ?? 0);
  const diagnosticCandidates = decoded.diagnostic_proxy_candidates ?? 0;
  const geometryCount = exported.building_elements_with_geometry ?? 0;
  const qualityLevel = confidence.level ?? 'unknown';

  statusPanelEl.appendChild(
    statusRow(
      'File',
      input.stream_count ? 'ok' : 'warn',
      input.revit_version
        ? `Opened Revit ${input.revit_version} · ${input.stream_count ?? 0} streams${parsedInSuffix(parseElapsedMs)}`
        : `Opened · ${input.stream_count ?? 0} streams${parsedInSuffix(parseElapsedMs)}`,
    ),
  );
  const failureMode = classifyFailureMode(diagnostics);
  statusPanelEl.appendChild(
    statusRow('Mode', failureMode.kind, `${failureMode.title} · ${failureMode.summary}`),
  );
  statusPanelEl.appendChild(
    statusRow(
      'Schema',
      input.has_formats_latest && input.has_global_latest ? 'ok' : 'warn',
      input.has_formats_latest && input.has_global_latest
        ? 'Schema and model streams found'
        : 'Required schema/model stream missing',
    ),
  );
  statusPanelEl.appendChild(
    statusRow(
      'Elements',
      validatedElements > 0 ? 'ok' : 'warn',
      validatedElements > 0
        ? `${validatedElements} validated elements decoded`
        : diagnosticCandidates > 0
          ? `No validated elements · ${diagnosticCandidates} diagnostic candidates`
          : 'No validated elements decoded',
    ),
  );
  statusPanelEl.appendChild(
    statusRow(
      'Geometry',
      geometryCount > 0 ? 'ok' : 'warn',
      geometryCount > 0
        ? `${geometryCount} elements have geometry`
        : 'No real-file element geometry decoded',
    ),
  );
  statusPanelEl.appendChild(
    statusRow(
      'IFC',
      qualityLevel === 'geometry' ? 'ok' : 'warn',
      `${exportQualityLabel(qualityLevel)}${typeof confidence.score === 'number' ? ` · ${Math.round(confidence.score * 100)}%` : ''}`,
    ),
  );
  statusPanelEl.appendChild(
    statusRow(
      'Warnings',
      warnings.length === 0 && unsupported.length === 0 ? 'ok' : 'warn',
      warningSummary(warnings, unsupported),
    ),
  );
}

type StatusKind = 'ok' | 'warn' | 'bad';

interface FailureModeStatus {
  kind: StatusKind;
  title: string;
  summary: string;
}

function classifyFailureMode(diagnostics: ExportDiagnostics): FailureModeStatus {
  const input = diagnostics.input ?? {};
  const decoded = diagnostics.decoded ?? {};
  const exported = diagnostics.exported ?? {};
  const confidence = diagnostics.confidence ?? {};
  const warnings = diagnostics.warnings ?? [];
  const unsupported = diagnostics.unsupported_features ?? [];
  const revitVersion = input.revit_version;
  const buildingElements = exported.building_elements ?? 0;
  const geometryElements = exported.building_elements_with_geometry ?? 0;
  const diagnosticCandidates = decoded.diagnostic_proxy_candidates ?? 0;
  const level = confidence.level ?? 'unknown';

  if (typeof revitVersion === 'number' && (revitVersion < 2016 || revitVersion > 2026)) {
    return {
      kind: 'warn',
      title: 'Unsupported Revit version',
      summary: 'outside the verified support range',
    };
  }
  if (level === 'unknown') {
    return {
      kind: 'bad',
      title: 'Parser bug, please report',
      summary: 'diagnostics did not include an export readiness level',
    };
  }
  if (input.has_formats_latest === false || input.has_global_latest === false) {
    return {
      kind: 'warn',
      title: 'Partial decode',
      summary: 'required schema/model streams were not decoded completely',
    };
  }
  if (buildingElements === 0 && diagnosticCandidates > 0) {
    return {
      kind: 'warn',
      title: 'Supported file, unsupported model layout',
      summary: 'only diagnostic candidates were found',
    };
  }
  if (level === 'scaffold' || buildingElements === 0) {
    return {
      kind: 'warn',
      title: 'Scaffold-only export',
      summary: 'no validated building elements were decoded',
    };
  }
  if (unsupported.length > 0 || warnings.length > 0 || geometryElements === 0) {
    return {
      kind: 'warn',
      title: 'Partial decode',
      summary: 'warnings, unsupported features, or missing geometry remain',
    };
  }
  return {
    kind: 'ok',
    title: 'Supported profile',
    summary: 'decoded output meets the current export profile',
  };
}

function statusRow(label: string, kind: StatusKind, value: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'status-row';
  const dot = document.createElement('span');
  dot.className = `status-dot ${kind}`;
  const labelEl = document.createElement('div');
  labelEl.className = 'status-label';
  labelEl.textContent = label;
  const valueEl = document.createElement('div');
  valueEl.className = 'status-value';
  valueEl.textContent = value;
  row.appendChild(dot);
  row.appendChild(labelEl);
  row.appendChild(valueEl);
  return row;
}

function warningSummary(warnings: string[], unsupported: string[]): string {
  if (warnings.length > 0) {
    const suffix = warnings.length > 1 ? ` · ${warnings.length - 1} more` : '';
    return `${warnings[0]}${suffix}`;
  }
  if (unsupported.length > 0) {
    const suffix = unsupported.length > 1 ? ` · ${unsupported.length - 1} more` : '';
    return `${unsupported[0]}${suffix}`;
  }
  return 'No export warnings';
}

function exportQualityLabel(level: string): string {
  switch (level) {
    case 'scaffold':
      return 'Scaffold';
    case 'typed_no_geometry':
      return 'Typed';
    case 'geometry':
      return 'Geometry';
    case 'diagnostic_partial':
      return 'Diagnostic';
    case 'proxy_only':
      return 'Proxy';
    default:
      return 'Unknown';
  }
}

function exportQualityClass(level: string): string {
  switch (level) {
    case 'geometry':
      return 'geometry';
    case 'typed_no_geometry':
      return 'typed';
    case 'diagnostic_partial':
    case 'proxy_only':
      return 'diagnostic';
    case 'scaffold':
    default:
      return 'scaffold';
  }
}

function countEntities(node: SceneNode): number {
  let n = node.entity_index !== null ? 1 : 0;
  for (const c of node.children) n += countEntities(c);
  return n;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------- Drag & drop / file picker ----------
pickBtn.addEventListener('click', () => fileInput.click());
fileInput.addEventListener('change', () => {
  const f = fileInput.files?.[0];
  if (f) void loadBytes(f);
});

['dragenter', 'dragover'].forEach((type) =>
  document.body.addEventListener(type, (ev) => {
    ev.preventDefault();
    dropzone.classList.add('drag-over');
  }),
);
['dragleave', 'drop'].forEach((type) =>
  document.body.addEventListener(type, (ev) => {
    ev.preventDefault();
    dropzone.classList.remove('drag-over');
  }),
);
document.body.addEventListener('drop', (ev) => {
  ev.preventDefault();
  const f = ev.dataTransfer?.files[0];
  if (!f) return;
  // Format gating lives in one place — loadBytes() calls the wasm
  // classifyFilename binding, backed by rvt::supported_profile — so
  // this handler and the file-picker path can't drift from each other.
  void loadBytes(f);
});

// ---------- Export buttons (VW1-16 / VW1-17 / VW1-11 surfaced) ----------

// The worker (worker.ts) has its own wasm instance and calls its own
// `init()` before touching any exported function — that's what makes
// `model` usable there. This module imports the same `../pkg/rvt.js`
// glue but is a *second*, separate instance: its exported functions
// are undefined until this thread also calls `init()` once. Skipping
// that was the whole bug behind "Export IFC"/"Export plan" failing on
// every file with `Cannot read properties of undefined (reading
// '__wbindgen_add_to_stack_pointer')" — the glue's internal `wasm`
// object was never set up on this thread.
let mainThreadWasmReady: Promise<typeof import('../pkg/rvt.js')> | null = null;
function ensureMainThreadWasm(): Promise<typeof import('../pkg/rvt.js')> {
  if (!mainThreadWasmReady) {
    mainThreadWasmReady = (async () => {
      const mod = await import('../pkg/rvt.js');
      await mod.default();
      return mod;
    })();
  }
  return mainThreadWasmReady;
}

function download(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on next tick so the download actually starts first.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

exportGlbBtn.addEventListener('click', () => {
  if (!lastGlb) return;
  const blob = new Blob([lastGlb.buffer as ArrayBuffer], {
    type: 'model/gltf-binary',
  });
  download(`${lastFileStem}.glb`, blob);
});

exportIfcBtn.addEventListener('click', () => {
  if (!model) return;
  // The IFC STEP writer is synchronous + fast; no worker hop needed
  // for the sample-family-sized models we've seen. If this ever blocks
  // the main thread on big projects, move it into worker.ts.
  void (async () => {
    const quality =
      currentDiagnostics && !isIfcDiagnostics(currentDiagnostics)
        ? exportQualityLabel(currentDiagnostics.confidence?.level ?? 'unknown').toLowerCase()
        : currentDiagnostics
          ? 'native ifc'
          : 'unknown';
    setStatus(`rendering IFC STEP · ${quality}`);
    try {
      const { modelToIfcStep } = await ensureMainThreadWasm();
      const text = modelToIfcStep(model as unknown as object);
      const blob = new Blob([text], { type: 'application/x-step' });
      download(`${lastFileStem}.ifc`, blob);
      setStatus(`exported ${lastFileStem}.ifc`);
    } catch (err) {
      setStatus(`IFC export failed: ${(err as Error).message ?? err}`);
    }
  })();
});

exportSvgBtn.addEventListener('click', () => {
  if (!model) return;
  void (async () => {
    setStatus('rendering plan SVG…');
    try {
      const { renderPlanSvg } = await ensureMainThreadWasm();
      const svg = renderPlanSvg(model as unknown as object, null);
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      download(`${lastFileStem}.svg`, blob);
      setStatus(`exported ${lastFileStem}.svg`);
    } catch (err) {
      setStatus(`plan export failed: ${(err as Error).message ?? err}`);
    }
  })();
});

downloadDiagnosticsBtn.addEventListener('click', () => {
  if (!currentDiagnostics) return;
  const json = JSON.stringify(currentDiagnostics, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  download(`${lastFileStem}.diagnostics.json`, blob);
  setStatus(`exported ${lastFileStem}.diagnostics.json`);
});

// Formats `["rvt", "rfa", "rte", "rft"]` as ".rvt, .rfa, .rte, and .rft" —
// the Oxford-comma style already used in `docs/supported-profile.md` and
// on this page before this list was pulled out of hand-written HTML.
function formatExtensionListOxford(extensions: string[]): string {
  const dotted = extensions.map((ext) => `.${ext}`);
  if (dotted.length <= 1) return dotted.join('');
  if (dotted.length === 2) return `${dotted[0]} and ${dotted[1]}`;
  return `${dotted.slice(0, -1).join(', ')}, and ${dotted[dotted.length - 1]}`;
}

// `viewer/index.html`'s file-picker `accept` filter, drop-zone hint, and
// "Supported profile" panel used to hand-copy the extension list that
// `classifyFilename` already enforces — the three could (and, per
// PER-1193, did) drift from each other and from `src/supported_profile.rs`.
// This reads the same `rvt::supported_profile::PROFILE` table via the
// `acceptedExtensions` wasm binding, so there is exactly one place that
// list is written.
async function applySupportedProfileToUi(): Promise<void> {
  const { acceptedExtensions } = await ensureMainThreadWasm();
  const extensions = acceptedExtensions() as string[];
  cachedAcceptedExtensions = extensions;
  fileInput.accept = extensions.map((ext) => `.${ext}`).join(',');
  fileMetaEl.textContent = `drop a ${extensions.map((ext) => `.${ext}`).join(' / ')} file anywhere`;
  supportedFormatsEl.textContent = `${formatExtensionListOxford(extensions)} metadata/schema across the verified 2016-2026 family corpus.`;
}
void applySupportedProfileToUi().catch((err: unknown) => {
  // Non-fatal: the placeholder copy in index.html stays generic instead
  // of naming extensions, and the file-picker falls back to accepting
  // any file. `loadBytes`'s own wasm-init try/catch is what actually
  // blocks a rejected format from loading.
  console.error('failed to load supported-profile UI text', err);
});

// ---------- Share link (VW1-24) ----------
// "How does a user get back to the same view without an account
// or a server?" — the answer is a URL fragment: camera pose +
// category filter, base64-encoded client-side, decoded back into
// the viewer on load. Nothing is uploaded; the file itself still
// has to be dropped again.

function cameraStateFromThree(): CameraState {
  const target = controls.target;
  const delta = camera.position.clone().sub(target);
  const distance = delta.length();
  const yaw = Math.atan2(delta.y, delta.x);
  const pitch = distance > 0 ? Math.asin(Math.min(1, Math.max(-1, delta.z / distance))) : 0;
  return {
    target: [target.x, target.y, target.z],
    distance,
    yaw,
    pitch,
    fov_radians: camera.fov * (Math.PI / 180),
    near: camera.near,
    far: camera.far,
  };
}

function applyRestoredState(state: ViewerState): void {
  hiddenTypes.clear();
  for (const t of state.category_filter?.hidden ?? []) hiddenTypes.add(t);
  renderCategories();
  applyCategoryVisibility();
  void refreshTree();
  void (async () => {
    const { cameraEye } = await ensureMainThreadWasm();
    const eye = cameraEye(state.camera as unknown as object) as unknown as [
      number,
      number,
      number,
    ];
    camera.position.set(eye[0], eye[1], eye[2]);
    controls.target.set(...state.camera.target);
    camera.fov = state.camera.fov_radians * (180 / Math.PI);
    camera.near = state.camera.near;
    camera.far = state.camera.far;
    camera.updateProjectionMatrix();
    controls.update();
  })();
}

shareLinkBtn.addEventListener('click', () => {
  void (async () => {
    try {
      const { encodeToFragment } = await ensureMainThreadWasm();
      const state: ViewerState = {
        camera: cameraStateFromThree(),
        view_mode: 'three_d',
        category_filter: { hidden: Array.from(hiddenTypes) },
      };
      const fragment = encodeToFragment(state as unknown as object);
      const url = `${location.origin}${location.pathname}#v=${fragment}`;
      await navigator.clipboard.writeText(url);
      setStatus('share link copied · drop the same file to restore this view');
    } catch (err) {
      setStatus(`share link failed: ${(err as Error).message ?? err}`);
    }
  })();
});

// Decode `#v=<fragment>` at load time; applied once the next file
// finishes parsing (there's nothing to restore a view *onto* until
// a model exists). Malformed/foreign hashes are ignored — VW1-24's
// encoding is versioned by top-level JSON keys, so a decode failure
// here just means "nothing to restore," not an error.
(function checkHashForRestore(): void {
  const hash = window.location.hash;
  if (!hash.startsWith('#v=')) return;
  const fragment = hash.slice(3);
  void (async () => {
    try {
      const { decodeFromFragment } = await ensureMainThreadWasm();
      const state = decodeFromFragment(fragment) as unknown as ViewerState | null;
      if (state && restoreEligible) pendingRestoreState = state;
    } catch {
      // Malformed fragment — leave pendingRestoreState unset.
    }
  })();
})();

renderEmptyStatusPanel();

// PER-1247 — surface which build is actually live, so "it's up" stops
// being a claim taken on faith. /version.json is written by
// deploy/Dockerfile.web from `git rev-parse HEAD` at image build time
// (see viewer/public/version.json for the `dev`-valued local placeholder
// it overwrites) — never hand-edited, so it can't quietly go stale.
interface BuildVersion {
  commit: string;
  commit_full: string;
  built_at: string | null;
}

void (async function loadBuildVersion(): Promise<void> {
  const buildVersionEl = $('build-version-text');
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const version = (await res.json()) as BuildVersion;
    buildVersionEl.innerHTML = '';
    const link = document.createElement('a');
    link.className = 'support-link';
    link.href = `https://github.com/ArturOPaes/revit/commit/${version.commit_full}`;
    link.textContent = version.commit;
    link.title = version.commit_full;
    buildVersionEl.appendChild(link);
    buildVersionEl.appendChild(
      document.createTextNode(version.built_at ? ` · built ${version.built_at}` : ' · local dev build'),
    );
  } catch {
    buildVersionEl.textContent = 'unknown (version.json unavailable)';
  }
})();
setStatus('ready · drop a file to begin');
